import React from 'react';
import { componentDebugger, ComponentDebugInfo } from '../utils/component-debug';

/**
 * Hook for debugging React components with automatic dimension and visibility tracking
 */
export function useComponentDebug(
  componentName: string,
  deps: React.DependencyList = [],
  additionalInfo?: Record<string, any>
) {
  const elementRef = React.useRef<HTMLElement>(null);
  const [debugInfo, setDebugInfo] = React.useState<ComponentDebugInfo | null>(null);
  const renderCountRef = React.useRef(0);

  React.useEffect(() => {
    renderCountRef.current += 1;
    
    const logDebugInfo = () => {
      const info = componentDebugger.logComponent(
        componentName, 
        elementRef, 
        {
          ...additionalInfo,
          renderCount: renderCountRef.current,
          dependencies: deps,
        }
      );
      setDebugInfo(info);
    };

    // Log immediately
    logDebugInfo();

    // Also log after a small delay to catch any async updates
    const timeoutId = setTimeout(logDebugInfo, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [componentName, ...deps]);

  return {
    elementRef,
    debugInfo,
    logNow: () => {
      const info = componentDebugger.logComponent(componentName, elementRef, additionalInfo);
      setDebugInfo(info);
      return info;
    }
  };
}

/**
 * Specialized hook for debugging file/content display components
 */
export function useFileDisplayDebug(
  componentName: string,
  fileCount: number,
  isLoading: boolean,
  additionalData?: Record<string, any>
) {
  return useComponentDebug(
    componentName,
    [fileCount, isLoading],
    {
      fileCount,
      isLoading,
      hasFiles: fileCount > 0,
      ...additionalData,
    }
  );
}

/**
 * Hook for debugging layout components specifically
 */
export function useLayoutDebug(
  componentName: string,
  layoutInfo: {
    isVisible?: boolean;
    expectedSize?: { width: number; height: number };
    children?: React.ReactNode;
  }
) {
  return useComponentDebug(
    componentName,
    [layoutInfo.isVisible, layoutInfo.expectedSize, layoutInfo.children],
    {
      expectedWidth: layoutInfo.expectedSize?.width,
      expectedHeight: layoutInfo.expectedSize?.height,
      hasChildren: React.Children.count(layoutInfo.children) > 0,
      childCount: React.Children.count(layoutInfo.children),
      isExpectedVisible: layoutInfo.isVisible,
    }
  );
}